'use client';

import type { RumGlobal } from '@datadog/browser-rum';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useIsBot } from '@/app/hooks/useIsBot';
import { IGNORE_ERRORS, IGNORE_PATTERN } from '@/app/hooks/useRUMRecording';
import { isLocalhost, isSynthetics } from '@/app/utils/host';
import { normalisePathname } from '@/app/utils/router';
import logger from '@/lib/logger';
interface RumErrorEvent {
  type?: string;
  message?: string;
  stack?: string;
}
const isIgnoreError = (error: RumErrorEvent): boolean => {
  return IGNORE_ERRORS.some(ignoreError => {
    return ignoreError.message && error.message && error.message.includes(ignoreError.message) || ignoreError.stack && error.stack && error.stack.includes(ignoreError.stack);
  });
};
export const useRUMRecording = () => {
  const pathname = usePathname();
  const [rum, setRUM] = useState<RumGlobal | null>(null);
  const isBot = useIsBot();
  useEffect(() => {
    if (!rum) return;
    const VIEW_NAME = normalisePathname(pathname);
    rum.startView({
      name: VIEW_NAME
    });
  }, [rum, pathname]);
  useEffect(() => {
    const init = async () => {
      try {
        if (isLocalhost() || isSynthetics() || isBot) return;
        const DATADOG_RUM = (await import('@datadog/browser-rum')).datadogRum;
        DATADOG_RUM.init({
          applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID as string,
          clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN as string,
          site: 'datadoghq.com',
          service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
          env: process.env.NEXT_PUBLIC_ENV,
          allowedTracingUrls: [
          // Allow tracing for your production site
          {
            match: /https:\/\/www\.isuzu-tis\.com/,
            propagatorTypes: ['datadog']
          },
          // Keep GTM server EXCLUDED from headers to prevent the CORS error
          {
            match: /https:\/\/server-gtm-prd\.isuzu-tis\.com/,
            propagatorTypes: []
          }],
          version: process.env.NEXT_PUBLIC_COMMIT_ID,
          sessionSampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_SAMPLE_RATE),
          trackUserInteractions: true,
          sessionReplaySampleRate: 0,
          trackResources: true,
          trackLongTasks: true,
          trackViewsManually: true,
          beforeSend: event => {
            if (event.type === 'error' && isIgnoreError(event.error)) {
              event.context = {
                ...event.context,
                ignoreError: 'true'
              };
            }
            return true;
          }
        });
        setRUM(DATADOG_RUM);
      } catch (error) {
        logger.error(`${IGNORE_PATTERN} failed to load @datadog/browser-rum: ${error}`);
      }
    };
    init();
  }, [isBot]);
};